// The plan's keys: one key…() per case, each true when it took the key, tried in
// the order main.ts's keydown dispatcher (`wireGlobalKeys`) lists them — Esc's
// cases first, the panel-field guard, Enter on what is under way, the sheet,
// the typed entry, the edit shortcuts, the nudges, Tab, the command bar, Enter,
// what is edited in place, Delete, Rotate, the repeat, Esc (docs/claude/tools.md,
// *The keys and the release*). Space's own stays in main.ts beside its pan.
import { matchesShortcut } from '../../shared/shortcuts';
import { cancelArmed } from './armedPick';
import { copySelection } from './clipboard';
import { updateCmdSuggest } from './commandBar';
import { cycleConstraintPiece } from './constraintUi';
import {
  invalidate,
  renderProperties,
  state,
  vp,
} from './core';
import { deleteSelected } from './deleteSelection';
import {
  commitDistEntry,
  drawDraft,
  pendingRectCorner,
  updateDistEntry,
} from './distanceEntry';
import {
  EDIT_RULES,
  editAllows,
} from '../editMode';
import {
  escUnderWay,
  finishUnderWay,
  takesTypedUnderWay,
} from './underWayUi';
import {
  channelNumberTarget,
  kitStep,
  lightTools,
} from './lightTools';
import {
  MULTI_KINDS,
  selectedCount,
} from '../selection';
import {
  activeSheet,
  sheetKeyDown,
} from './sheetSpace';
import {
  finishTextComment,
  textBoxes,
} from './textBoxes';
import {
  copyTitleObjSelection,
  deleteSelectedTitleObj,
  selectTitleObjOnly,
  setTitleEdit,
  titleEditor,
} from './titleEditor';
import { sculptTakesNumber } from './sculptBenchUi';
import {
  $,
  cutSelection,
  cycleSectionAt,
  cycleSelectionAt,
  drillingSectionAt,
  duplicateSelection,
  EDIT_LIFE,
  escapeTool,
  finishWork,
  groupSelection,
  lastScreen,
  liveSpace,
  nudgeSelection,
  nudgeSelectionUp,
  pointerOnCanvas,
  redo,
  repeatLastTool,
  rotateSelected,
  selectAll,
  showShortcutsHelp,
  SPACES,
  textEditorUi,
  undo,
  ungroupSelection,
} from '../main';

/** Esc lets go of an armed pick first — even from a panel field (a relation's
 *  button, a part's polygons with the product editor hidden). */
export function keyEscArmedPick(e: KeyboardEvent): boolean {
  if (e.key === 'Escape' && cancelArmed()) { e.preventDefault(); return true; }
  return false;
}

/** What is under way — a drag, a handle's or a grip's work, held or armed — goes
 *  back where it began, even from a panel field; a figure being typed for it
 *  owns Esc first (cleared here). */
export function keyEscUnderWay(e: KeyboardEvent): boolean {
  if (e.key !== 'Escape') return false;
  const esc = escUnderWay(state.distEntry != null);
  if (esc === 'entry') { state.distEntry = null; updateDistEntry(); }
  if (esc) e.preventDefault();
  return !!esc;
}

/** A panel field, a list or a text area keeps every key after those two. */
export function keyInField(e: KeyboardEvent): boolean {
  const target = e.target as HTMLElement;
  return target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA';
}

/** Enter ends a handle's or a grip's work where it is (a typed figure, below,
 *  owns it while one is being typed). */
export function keyEnterUnderWay(e: KeyboardEvent): boolean {
  if (e.key === 'Enter' && state.distEntry == null && finishUnderWay()) { e.preventDefault(); return true; }
  return false;
}

/** Keyboard-shortcuts cheat sheet: '?' toggles it; Escape closes it. */
export function keyHelp(e: KeyboardEvent): boolean {
  if (matchesShortcut(e, 'help')) { e.preventDefault(); showShortcutsHelp(); return true; }
  return false;
}

/** On a sheet: its keys first (sheetKeyDown). */
export function keySheet(e: KeyboardEvent): boolean {
  const shKey = activeSheet();
  return !!shKey && !titleEditor.titleEdit && sheetKeyDown(e, shKey);
}

/** Title-block object editor: Delete removes the selected block, Escape clears
 *  the selection (a second Escape exits edit mode). */
export function keyTitleEditor(e: KeyboardEvent): boolean {
  if (titleEditor.titleEdit) {
    if (matchesShortcut(e, 'delete') && titleEditor.selectedTitleObjIds.length) { e.preventDefault(); deleteSelectedTitleObj(); return true; }
    if (matchesShortcut(e, 'copy')) { copyTitleObjSelection(); return true; }
    // Ctrl+V (title-block object paste) is handled by the window 'paste' listener.
    if (e.key === 'Escape') { e.preventDefault(); if (titleEditor.selectedTitleObjIds.length) { selectTitleObjOnly(null); renderProperties(); invalidate(); } else setTitleEdit(false); return true; }
  }
  return false;
}

/** Direct distance entry while drawing a polygon/wall/line vertex (after the
 *  first vertex): type a distance, Enter places the next vertex that far along
 *  the pointing direction, Esc cancels. Units mm/cm/m/ft/in (default mm). */
export function keyTypedEntry(e: KeyboardEvent): boolean {
  if ((drawDraft() || pendingRectCorner() || state.tool === 'offset' || takesTypedUnderWay()) && !e.ctrlKey && !e.metaKey && !e.altKey) {
    // Begin an entry with a digit / dot / '@' / '-'; once started also accept
    // ',', '<', unit letters and arithmetic, so coordinate and polar forms and
    // sums (parseMeasure) can be typed.
    const start = /^[0-9.@-]$/.test(e.key);
    const cont = state.distEntry != null && /^[0-9.,<@a-z+*/()-]$/i.test(e.key);
    if (start || cont) { e.preventDefault(); state.distEntry = (state.distEntry ?? '') + e.key.toLowerCase(); updateDistEntry(); return true; }
    if (state.distEntry != null) {
      if (e.key === 'Enter') { e.preventDefault(); commitDistEntry(); return true; }
      if (e.key === 'Escape') { e.preventDefault(); state.distEntry = null; updateDistEntry(); return true; }
      if (e.key === 'Backspace') { e.preventDefault(); state.distEntry = state.distEntry.slice(0, -1) || null; updateDistEntry(); return true; }
    }
  }
  return false;
}

/** Undo / redo. Handled here (not via a native menu accelerator) so the key
 *  works identically in the desktop and web builds; the Edit menu items keep
 *  their shortcut labels but don't register the accelerator (no double-fire). */
export function keyUndoRedo(e: KeyboardEvent): boolean {
  if (matchesShortcut(e, 'undo')) { e.preventDefault(); undo(); return true; }
  if (matchesShortcut(e, 'redo')) { e.preventDefault(); redo(); return true; }
  return false;
}

/** Cut lets the browser's own 'cut' event through (no preventDefault), so the
 *  window 'cut' listener can put the payload on the system clipboard. */
export function keyCut(e: KeyboardEvent): boolean {
  if (matchesShortcut(e, 'cut')) { cutSelection(); return true; }
  return false;
}

export function keyDuplicate(e: KeyboardEvent): boolean {
  if (matchesShortcut(e, 'duplicate')) { e.preventDefault(); duplicateSelection(); return true; }
  return false;
}

export function keySelectAll(e: KeyboardEvent): boolean {
  if (matchesShortcut(e, 'selectAll')) { e.preventDefault(); selectAll(); return true; }
  return false;
}

export function keyGroup(e: KeyboardEvent): boolean {
  if (matchesShortcut(e, 'group')) { e.preventDefault(); groupSelection(); return true; }
  if (matchesShortcut(e, 'ungroup')) { e.preventDefault(); void ungroupSelection(); return true; }
  return false;
}

/** Copy the selected object(s) — a grouped object copies its group. Paste
 *  (Ctrl+V) is handled by the window 'paste' listener, so a clipboard image and
 *  an object paste can never both fire from one keystroke. */
export function keyCopy(e: KeyboardEvent): boolean {
  if (matchesShortcut(e, 'copy')) { copySelection(); return true; }
  return false;
}

/** Arrow keys nudge the selection (10 mm, Shift = 100 mm). World Y is up. */
export function keyNudge(e: KeyboardEvent): boolean {
  if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.startsWith('Arrow')
      && state.tool === 'select' && editAllows(state.editing, 'nudge') && selectedCount(state, MULTI_KINDS) > 0) {
    e.preventDefault();
    const s = e.shiftKey ? 100 : 10;
    const d = e.key === 'ArrowLeft' ? [-s, 0] : e.key === 'ArrowRight' ? [s, 0] : e.key === 'ArrowUp' ? [0, s] : e.key === 'ArrowDown' ? [0, -s] : null;
    if (d) nudgeSelection(d[0], d[1]);
    return true;
  }
  return false;
}

/** Page Up / Page Down nudge it up and down, the same steps. */
export function keyNudgeUpDown(e: KeyboardEvent): boolean {
  if (!e.ctrlKey && !e.metaKey && !e.altKey && (e.key === 'PageUp' || e.key === 'PageDown')
      && state.tool === 'select' && editAllows(state.editing, 'nudge') && selectedCount(state, MULTI_KINDS) > 0) {
    e.preventDefault();
    const s = e.shiftKey ? 100 : 10;
    nudgeSelectionUp(e.key === 'PageUp' ? s : -s);
    return true;
  }
  return false;
}

/** In the text tool, once both corners are placed, typing a printable
 *  character creates the box without a leader arrow and captures the text
 *  (instead of the keystroke triggering a global shortcut). */
export function keyTextBoxTyping(e: KeyboardEvent): boolean {
  if (state.tool === 'text' && textBoxes.textDraft.a && textBoxes.textDraft.b &&
      e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    e.preventDefault();
    textBoxes.textDraft.leader = undefined; // typing skips the arrow
    const ch = e.key;
    finishTextComment(); // opens the inline editor
    textEditorUi.type(ch);
    return true;
  }
  return false;
}

/** Tab / Shift+Tab with the pointer on the drawing: cycle through the objects
 *  stacked under it (Alt+click does the same with the mouse). */
export function keyCycleSelection(e: KeyboardEvent): boolean {
  if (matchesShortcut(e, 'cycleSelection') && !activeSheet() && state.tool === 'select' && pointerOnCanvas) {
    e.preventDefault();
    const world = vp.screenToWorld(lastScreen), dir = e.shiftKey ? -1 : 1;
    // Inside a drilled-into section view, through what it shows there.
    const sec = state.sectionDrill ? drillingSectionAt(lastScreen) : null;
    if (!sec || !cycleSectionAt(sec, world, dir)) cycleSelectionAt(world, lastScreen, dir);
    return true;
  }
  return false;
}

/** …and with the Constrain tool, through the PIECES stacked under it (a room's
 *  outer and inner faces, a corner shared by two objects). */
export function keyCycleConstraintPiece(e: KeyboardEvent): boolean {
  if (matchesShortcut(e, 'cycleSelection') && !activeSheet() && state.tool === 'constrain' && pointerOnCanvas) {
    e.preventDefault();
    cycleConstraintPiece(lastScreen, e.shiftKey ? -1 : 1);
    return true;
  }
  return false;
}

/** Command bar: a letter typed on the drawing starts a command, AutoCAD-style —
 *  it goes into the bar, which then owns the keyboard until Enter / Space / Esc.
 *  (A typed number keeps its own path above; a sheet keeps its own tools.) The
 *  Channel tool laying runs takes a number there too: how many. */
export function keyCommandLetter(e: KeyboardEvent): boolean {
  const runsCount = !!channelNumberTarget() && /^[0-9]$/.test(e.key);
  const skyKit = state.tool === 'light-sky' && !lightTools.skyDraft.length && !kitStep && /^[0-9]$/.test(e.key);
  const sculptFigure = sculptTakesNumber() && /^[0-9.]$/.test(e.key);
  if (!activeSheet() && (/^[a-z]$/i.test(e.key) || runsCount || skyKit || sculptFigure) && !e.ctrlKey && !e.metaKey && !e.altKey) {
    e.preventDefault();
    const cmd = $<HTMLInputElement>('cmd-input');
    if (cmd.classList.contains('typed-entry')) { state.distEntry = null; updateDistEntry(); } // a command replaces it
    cmd.value += e.key;
    cmd.focus();
    updateCmdSuggest();
    return true;
  }
  return false;
}

/** Every tool: Enter (or a right-click / double-click) finishes its work in
 *  progress (`finishWork`); Escape is `escapeTool`. */
export function keyFinishWork(e: KeyboardEvent): boolean {
  if (e.key === 'Enter' && finishWork()) {
    e.preventDefault();
    return true;
  }
  return false;
}

/** Esc, and Enter where its row says Enter finishes it too, ends what is
 *  edited in place (EDIT_LIFE / EDIT_RULES). */
export function keyEditMode(e: KeyboardEvent): boolean {
  const editing = state.editing;
  if (editing && (e.key === 'Escape' || (e.key === 'Enter' && EDIT_RULES[editing.kind].enterLeaves))) {
    EDIT_LIFE[editing.kind].esc();
    return true;
  }
  // A mode Enter does not leave may still take it (the drawing editor: Done).
  if (editing && e.key === 'Enter' && EDIT_LIFE[editing.kind].enter?.()) {
    e.preventDefault();
    return true;
  }
  return false;
}

export function keyDelete(e: KeyboardEvent): boolean {
  if (!matchesShortcut(e, 'delete')) return false;
  // Drawing editor: Delete removes the selected local shape, never the model behind.
  if (liveSpace() === 'part') { SPACES.part.del(); return true; }
  deleteSelected(); // a sub-selected door / pane / opening goes, not its room
  return true;
}

export function keyRotate(e: KeyboardEvent): boolean {
  if (matchesShortcut(e, 'rotateCcw')) { rotateSelected(-15); return true; }
  if (matchesShortcut(e, 'rotateCw')) { rotateSelected(15); return true; }
  return false;
}

/** Nothing is running: Enter / Space repeats the last tool (AutoCAD). A focused
 *  toolbar button is blurred first, or the key's own keyup would click it too. */
export function keyRepeatLast(e: KeyboardEvent): boolean {
  if (!(matchesShortcut(e, 'repeatLast') && state.tool === 'select' && !activeSheet())) return false;
  const target = e.target as HTMLElement;
  e.preventDefault();
  if (target.tagName === 'BUTTON') target.blur();
  repeatLastTool();
  return true;
}

export function keyEscapeTool(e: KeyboardEvent): boolean {
  if (e.key !== 'Escape') return false;
  escapeTool();
  return true;
}
